import axios from 'axios';
import configData from '../configure';
import { getOrgUpiId } from '../config/orgUpiConfig';

/**
 * Format phone number to standard E.164 (e.g. +919876543210)
 */
export function formatE164PhoneNumber(rawPhone) {
    if (!rawPhone) return '';
    let cleaned = String(rawPhone).replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+')) {
        return cleaned;
    }
    if (cleaned.length === 10) {
        return `+91${cleaned}`;
    }
    if (cleaned.length === 12 && cleaned.startsWith('91')) {
        return `+${cleaned}`;
    }
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
}

/**
 * Helper to get clean JWT token without 'Bearer ' prefix for backend verification
 */
export function getAuthToken(token) {
    let t = token || (typeof window !== 'undefined' && localStorage ? localStorage.getItem('token') : '') || '';
    if (!t) return '';
    if (t.startsWith('Bearer ')) {
        t = t.substring(7);
    }
    return t;
}

/**
 * Helper to ensure orgName is a clean display name and not a raw UUID
 */
export function cleanOrgName(rawName, orgId) {
    if (rawName && typeof rawName === 'string') {
        const trimmed = rawName.trim();
        const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(trimmed);
        if (!isUuid && trimmed.length > 0) {
            return trimmed;
        }
    }
    try {
        if (typeof window !== 'undefined' && window.localStorage && orgId) {
            const stored = localStorage.getItem(`org_name_${orgId}`) || localStorage.getItem('org_name');
            if (stored && !/^[0-9a-fA-F]{8}-/.test(stored.trim())) {
                return stored.trim();
            }
        }
    } catch (e) {}
    return '';
}

/**
 * Send Fee Payment Reminder WhatsApp message via Meta WABA template
 */
export async function sendWhatsAppFeeReminder({
    orgId,
    orgName,
    phoneNumber,
    parentName,
    studentName,
    studentId,
    userId,
    installmentId,
    classId,
    className,
    feeStructureId,
    academicYear,
    pendingAmount,
    amount,
    upiId,
    dueDate = 'Immediate',
    paymentUrl = 'https://org1.egurukulapps.com',
    token
}) {
    const formattedPhone = formatE164PhoneNumber(phoneNumber);
    if (!formattedPhone || formattedPhone.length < 10) {
        throw new Error('Invalid phone number for WhatsApp message.');
    }

    const authHeader = getAuthToken(token);
    const finalAmount = Number(pendingAmount !== undefined ? pendingAmount : (amount || 0));
    const activeUpiId = upiId || getOrgUpiId(orgId);
    const activeOrgName = cleanOrgName(orgName, orgId);

    // Try dedicated /feepayment/whatsapp-reminder endpoint first
    try {
        const reminderUrl = configData.WHATSAPP_FEE_REMINDER || `${configData.INFRASTRUCTURE_FEEPAYMENT}/whatsapp-reminder`;
        const reminderPayload = {
            orgId: orgId || 'DEFAULT',
            orgName: activeOrgName,
            userId: userId || studentId || 'PARENT_USER',
            studentId: studentId || 'STUDENT_UNKNOWN',
            installmentId: installmentId || 'INST_1',
            classId: classId || 'CLASS_1',
            studentName: studentName || 'Student',
            className: className || 'Class',
            amount: finalAmount > 0 ? finalAmount : 1,
            upiId: activeUpiId,
            phoneNumber: formattedPhone,
            academicYear: academicYear || '2026-2027',
            feeStructureId: feeStructureId || 'STRUCTURE_1'
        };

        const res = await axios.post(reminderUrl, reminderPayload, {
            headers: {
                'Authorization': authHeader,
                'Content-Type': 'application/json'
            }
        });
        return res.data;
    } catch (apiErr) {
        console.warn("Dedicated /feepayment/whatsapp-reminder failed, trying template endpoint:", apiErr);
        const targetUrl = typeof configData.WHATSAPP_SEND_TEMPLATE === 'function'
            ? configData.WHATSAPP_SEND_TEMPLATE('FEE_PAYMENT_REMINDER')
            : `${configData.INFRASTRUCTURE_FEEPAYMENT?.replace('/feepayment', '')}/whatsapp/messages/FEE_PAYMENT_REMINDER`;

        const activeUpiId = upiId || getOrgUpiId(orgId) || '';
        const fallbackPayload = {
            orgId: orgId || 'DEFAULT',
            recipients: [
                {
                    phoneNumber: formattedPhone,
                    params: {
                        student_name: studentName || 'Student',
                        class_name: className || 'Class',
                        amount: `₹${finalAmount}`,
                        upi_id: activeUpiId,
                        org_name: activeOrgName
                    }
                }
            ]
        };

        const res = await axios.post(targetUrl, fallbackPayload, {
            headers: {
                'Authorization': authHeader,
                'Content-Type': 'application/json'
            }
        });

        return res.data;
    }
}

/**
 * Send Bulk Fee Payment Reminder WhatsApp messages to multiple students
 */
export async function sendBulkWhatsAppFeeReminders({ orgId, orgName, recipients = [], token }) {
    const authHeader = getAuthToken(token);
    const defaultOrgUpi = getOrgUpiId(orgId);
    const activeOrgName = cleanOrgName(orgName, orgId);

    const validRecipients = recipients
        .map(r => ({
            phoneNumber: formatE164PhoneNumber(r.phoneNumber),
            params: {
                student_name: r.studentName || 'Student',
                class_name: r.className || 'Class',
                amount: `₹${r.pendingAmount || r.amount || '0'}`,
                upi_id: r.upiId || defaultOrgUpi,
                org_name: cleanOrgName(r.orgName, orgId) || activeOrgName
            }
        }))
        .filter(r => r.phoneNumber && r.phoneNumber.length >= 10);

    const payload = {
        orgId: orgId || 'DEFAULT',
        recipients: validRecipients
    };

    const targetUrl = typeof configData.WHATSAPP_SEND_TEMPLATE === 'function'
        ? configData.WHATSAPP_SEND_TEMPLATE('FEE_PAYMENT_REMINDER')
        : `${configData.INFRASTRUCTURE_FEEPAYMENT?.replace('/feepayment', '')}/whatsapp/messages/FEE_PAYMENT_REMINDER`;

    const res = await axios.post(targetUrl, payload, {
        headers: {
            'Authorization': getAuthToken(token),
            'Content-Type': 'application/json'
        }
    });

    return res.data;
}

/**
 * Send Class Schedule Reminder WhatsApp message via Meta WABA template CLASS_SCHEDULE_REMINDER
 * Template placeholders: recipient_name, course_or_subject, class_name, date_time, org_name
 */
export async function sendWhatsAppClassScheduleReminder({
    orgId,
    orgName,
    phoneNumber,
    recipientName,
    courseOrSubject,
    className,
    dateTime,
    token
}) {
    const formattedPhone = formatE164PhoneNumber(phoneNumber);
    const activeOrgName = cleanOrgName(orgName, orgId);
    const authHeader = getAuthToken(token);

    try {
        const targetUrl = typeof configData.WHATSAPP_SEND_TEMPLATE === 'function'
            ? configData.WHATSAPP_SEND_TEMPLATE('CLASS_SCHEDULE_REMINDER')
            : `${configData.INFRASTRUCTURE_FEEPAYMENT?.replace('/feepayment', '')}/whatsapp/messages/CLASS_SCHEDULE_REMINDER`;

        const payload = {
            orgId: orgId || 'DEFAULT',
            recipients: [
                {
                    phoneNumber: formattedPhone || '+919876543210',
                    params: {
                        recipient_name: recipientName || 'Student',
                        course_or_subject: courseOrSubject || 'Class',
                        class_name: className || 'Class',
                        date_time: dateTime || 'Upcoming Class',
                        org_name: activeOrgName
                    }
                }
            ]
        };

        const res = await axios.post(targetUrl, payload, {
            headers: {
                'Authorization': authHeader,
                'Content-Type': 'application/json'
            }
        });
        return res.data;
    } catch (apiErr) {
        console.warn("API dispatch for CLASS_SCHEDULE_REMINDER template failed, falling back to direct WhatsApp Web:", apiErr);
        const msgText = `Hi ${recipientName || 'Student'},\n\nThis is a reminder for your upcoming class starting in 1 hour:\n\n*Subject*: ${courseOrSubject || 'Subject'}\n*Class*: ${className || 'Class'}\n*Date & Time*: ${dateTime || 'Upcoming Class'}\n\nKindly ensure you join on time. Reach out if you have any questions!\n\nRegards,\n${activeOrgName} Team`;
        if (formattedPhone && formattedPhone.length >= 10) {
            openDirectWhatsAppWeb(formattedPhone, msgText);
        } else {
            window.open(`https://wa.me/?text=${encodeURIComponent(msgText)}`, '_blank');
        }
        return { fallbackUsed: true };
    }
}

/**
 * Fetch WhatsApp delivery log for a single phone number
 */
export async function getWhatsAppMessageLog(phoneNumber, token) {
    const formattedPhone = formatE164PhoneNumber(phoneNumber);
    const targetUrl = `${configData.WHATSAPP_MESSAGE_LOG || `${configData.INFRASTRUCTURE_FEEPAYMENT?.replace('/feepayment', '')}/whatsapp/messages/log`}?phoneNumber=${encodeURIComponent(formattedPhone)}`;

    const res = await axios.get(targetUrl, {
        headers: {
            'Authorization': getAuthToken(token)
        }
    });
    return res.data;
}

/**
 * Open Direct WhatsApp Web / App chat with prefilled message (Instant fallback)
 */
export function openDirectWhatsAppWeb(phoneNumber, messageText) {
    const cleanPhone = String(phoneNumber || '').replace(/[^0-9]/g, '');
    const phoneWithCountry = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    const url = `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(messageText || '')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * 2. Fetch Review Queue of WhatsApp payment screenshots for an organization
 */
export async function getWhatsAppPaymentScreenshots(orgId, classId, token) {
    if (!orgId) throw new Error("orgId is required");
    const authHeader = getAuthToken(token);
    let targetUrl = `${configData.WHATSAPP_PAYMENT_SCREENSHOTS || `${configData.INFRASTRUCTURE_FEEPAYMENT}/whatsapp-screenshots`}?orgId=${encodeURIComponent(orgId)}`;
    if (classId && classId !== 'ALL') {
        targetUrl += `&classId=${encodeURIComponent(classId)}`;
    }

    const res = await axios.get(targetUrl, {
        headers: {
            'Authorization': authHeader
        }
    });
    return Array.isArray(res.data) ? res.data : [];
}

/**
 * 3. Manually attach an unmatched WhatsApp screenshot to a specific FeePayment
 */
export async function attachWhatsAppScreenshot({ wamid, feePaymentId }, token) {
    if (!wamid || !feePaymentId) throw new Error("Both wamid and feePaymentId are required");
    const authHeader = getAuthToken(token);
    const targetUrl = configData.WHATSAPP_SCREENSHOT_ATTACH || `${configData.INFRASTRUCTURE_FEEPAYMENT}/whatsapp-screenshots/attach`;

    const res = await axios.post(targetUrl, { wamid, feePaymentId }, {
        headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
        }
    });
    return res.data;
}

/**
 * 4. Approve (SUCCESS) or Reject (FAILED) a Fee Payment
 */
export async function updateFeePaymentStatus(feePaymentsId, { paymentStatus, remarks, verifiedBy }, token) {
    if (!feePaymentsId) throw new Error("feePaymentsId is required");
    const authHeader = getAuthToken(token);
    const targetUrl = `${configData.INFRASTRUCTURE_FEEPAYMENT}/${feePaymentsId}`;

    const payload = {
        paymentStatus,
        remarks: remarks || (paymentStatus === 'SUCCESS' ? 'Approved via WhatsApp Review' : 'Rejected via WhatsApp Review'),
        paymentMetaData: verifiedBy ? { verifiedBy } : {}
    };

    const res = await axios.put(targetUrl, payload, {
        headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json'
        }
    });
    return res.data;
}

